// Cancel confirmation — RON-5466 Phase 3, rebuilt to the prototype 04-November-2026.
//
// Prototype: `CancelScreen` (single, with the group chooser) and `BulkCancel`.
//
// TWO STEPS, not one. A visit booked alongside colleagues asks WHAT to cancel before it asks
// whether to go ahead; a solo visit has nothing to choose and opens straight on the confirmation.
// This replaced an "also cancel the N people invited with them" checkbox, which put the question
// and the answer on the same screen and listed the colleagues only after the host had already
// agreed to cancel them.
//
// Still serves the bulk screen, which has no chooser: a bulk selection is already an explicit
// list of visits.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import VisitorDetailRow from "./VisitorDetailRow.jsx";
import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { canCancelVisit, pluralText } from "../visitorConstants.js";
import { VisitorAvatar, VisitorStatusBadge } from "./VisitorBadges.jsx";

// The prototype's red InfoBanner. A triangle rather than the circled "i" the other colours use —
// the shape alone has to carry "this is not reversible" for anyone who cannot see the red.
const DangerBanner = ({ children }) => (
  <div className="visitor-banner visitor-banner--danger">
    <svg
      className="visitor-banner__icon"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
    <span>{children}</span>
  </div>
);

// The disc on each scope card. One person or a group — the prototype uses the same mark for
// both and lets the wording carry the difference.
const ScopeMark = () => (
  <span className="visitor-cancel__scope-mark" aria-hidden="true">
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  </span>
);

const VisitorCancelConfirm = ({
  visits,
  groupSiblings = [],
  includeGroup = false,
  // Takes the chosen scope rather than flipping a flag: the chooser is a decision, not a toggle,
  // and the caller needs to know a decision was made at all.
  onSelectGroup,
  // True on the bulk screen. Passed rather than inferred from `visits.length`, because a bulk
  // selection of one row is still a bulk cancel and has to read like one.
  isBulk = false,
  // §8/§9, 24-October-2026. ON by default, matching the server's own default and the prototype.
  sendEmail = true,
  onToggleEmail,
  isSubmitting,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslation("common");

  // Whether the host has ANSWERED the chooser — not the answer itself, which lives in the caller
  // as `includeGroup`. Keeping it this way round means siblings arriving on a later list refresh
  // re-open the question instead of being silently cancelled or silently dropped.
  const [scopeChosen, setScopeChosen] = useState(false);

  const visit = visits[0];
  const hasGroup = groupSiblings.length > 0;
  const choosing = hasGroup && !scopeChosen;

  // The rows the server will actually act on, so the counts on screen match the outcome.
  const targets = includeGroup ? [...visits, ...groupSiblings] : visits;
  const cancellable = targets.filter(canCancelVisit);
  const skipped = targets.length - cancellable.length;

  const chooseScope = (include) => () => {
    onSelectGroup?.(include);
    setScopeChosen(true);
  };

  if (targets.length === 0) {
    return (
      <div className="visitor-cancel">
        <p className="visitor__placeholder">{t("react_visitors_not_found")}</p>
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
        >
          {t("react_visitors_back_to_list")}
        </button>
      </div>
    );
  }

  // ── Step 1: just this visitor, or the whole group? ──────────────────────────
  if (choosing) {
    const partySize = groupSiblings.length + 1;

    return (
      <div className="visitor-cancel">
        <DangerBanner>
          {t("react_visitors_cancel_group_intro")
            .replace("%s", visit.name)
            .replace("%n", String(partySize))}
        </DangerBanner>

        {/* Named before the question, not after it. The whole point of this step is that the host
            sees who "the group" is while deciding, rather than agreeing to a number. */}
        <ul className="visitor-cancel__members">
          {groupSiblings.map((member) => (
            <li className="visitor-cancel__member" key={member.id}>
              <VisitorAvatar name={member.name} type={member.type} />
              <span className="visitor-cancel__name">{member.name}</span>
            </li>
          ))}
        </ul>

        {/* Buttons, where the prototype uses clickable divs. Same behaviour, but reachable by
            keyboard and announced as choices — this is the only way past this screen. */}
        <button
          type="button"
          className="visitor-cancel__scope"
          onClick={chooseScope(false)}
        >
          <ScopeMark />
          <span className="visitor-cancel__scope-text">
            <span className="visitor-cancel__scope-title">
              {t("react_visitors_cancel_scope_single")}
            </span>
            <span className="visitor-cancel__scope-hint">
              {t("react_visitors_cancel_scope_single_hint")}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="visitor-cancel__scope visitor-cancel__scope--danger"
          onClick={chooseScope(true)}
        >
          <ScopeMark />
          <span className="visitor-cancel__scope-text">
            <span className="visitor-cancel__scope-title">
              {t("react_visitors_cancel_scope_group")}
            </span>
            <span className="visitor-cancel__scope-hint">
              {t("react_visitors_cancel_scope_group_hint").replace(
                "%s",
                String(partySize)
              )}
            </span>
          </span>
        </button>

        <div className="visitor-wizard__actions">
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={onBack}
            disabled={isSubmitting}
          >
            {t("react_visitors_keep_booking")}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: confirm ─────────────────────────────────────────────────────────

  const warning = includeGroup
    ? t("react_visitors_cancel_warning_group").replace("%s", String(targets.length))
    : t(
        isBulk
          ? "react_visitors_cancel_warning_bulk"
          : "react_visitors_cancel_warning"
      );

  // A mixed selection is not an error — rows that can no longer be cancelled (already closed, or
  // the guest has arrived) are simply left alone. §4.9 reports the same thing as `skipped`.
  // ONE BOOKING GETS THE DETAIL ROWS, MORE THAN ONE GETS THE LIST — and the bulk screen is no
  // longer what decides it.
  //
  // The group confirmation used to show the prototype's four rows with the party written into
  // the first one: `Sarah Mitchell + 2 others`, then ONE date, ONE time and ONE location, all
  // taken from Sarah. The prototype's own fixtures share a date, so it never showed there. Ours
  // do not: that group is spread over three dates, three arrival times and two cities, so the
  // card stated three facts that were wrong for two of the three people it was about to cancel.
  //
  // A deliberate departure from the prototype, on the one screen that cannot be undone.
  const showList = targets.length > 1;

  const skippedHint = skipped > 0 && (
    <p className="visitor-card__hint">
      {pluralText(t, "react_visitors_cancel_skipped", skipped)}
    </p>
  );

  return (
    <div className="visitor-cancel">
      {/* Outside the card on the single screen, inside it on the bulk one — the prototype does
          exactly this, and it reads correctly both times: here the warning is about the booking
          described below it, there it heads the list it applies to. */}
      {!isBulk && <DangerBanner>{warning}</DangerBanner>}

      <div className="visitor-card">
        {isBulk && <DangerBanner>{warning}</DangerBanner>}

        {showList ? (
          <ul className="visitor-review__people">
            {targets.map((row) => (
              <li className="visitor-cancel__row" key={row.id}>
                <VisitorAvatar name={row.name} type={row.type} />
                <span className="visitor-cancel__name">{row.name}</span>
                {/* The prototype's second column. Two guests sharing a first name are otherwise
                    indistinguishable on the one screen that cannot be undone. */}
                {row.company && (
                  <span className="visitor-cancel__company">{row.company}</span>
                )}
                <span className="visitor-cancel__date">
                  {formatDateToEEEddMMM(row.startDate)}
                </span>
                {/* Kept, though the prototype has no equivalent: it is what tells the host which
                    of these rows the `skipped` line below is about. */}
                <VisitorStatusBadge status={row.status} />
              </li>
            ))}
          </ul>
        ) : (
          <>
            <VisitorDetailRow
              label={t("react_visitors_field_visitor")}
              value={visit.name}
            />
            <VisitorDetailRow
              label={t("date")}
              value={
                visit.endDate && visit.endDate !== visit.startDate
                  ? `${formatDateToEEEddMMM(
                      visit.startDate
                    )} – ${formatDateToEEEddMMM(visit.endDate)}`
                  : formatDateToEEEddMMM(visit.startDate)
              }
            />
            <VisitorDetailRow label={t("time")} value={visit.arrivalTime || "—"} />
            <VisitorDetailRow
              label={t("react_visitors_field_site")}
              // The prototype has one site per visit and we have buildings underneath it. Joined
              // rather than split into a fifth row, so the card keeps the prototype's shape.
              value={[visit.site, visit.building].filter(Boolean).join(" · ") || "—"}
            />
          </>
        )}

        {skippedHint}
      </div>

      {/* One `visit_cancelled` email per visitor ACTUALLY cancelled — the skipped ones, whose
          guest has already arrived, are not emailed. Which is why this sits below the skipped
          notice: the host has just been told the count it applies to. */}
      {cancellable.length > 0 && (
        <div className="visitor-card">
          <label className="visitor-toggle">
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {t(
                  cancellable.length > 1
                    ? "react_visitors_send_cancel_email_all"
                    : "react_visitors_send_cancel_email"
                )}
              </span>
              <span className="visitor-toggle__hint">
                {t(
                  cancellable.length > 1
                    ? "react_visitors_send_cancel_email_all_hint"
                    : "react_visitors_send_cancel_email_hint"
                )}
              </span>
            </span>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={onToggleEmail}
              className="visitor-toggle__input"
            />
            {/* The switch the host sees. The input above is clipped to a pixel by the stylesheet,
                so without these two spans this card renders a label and empty space. */}
            <span className="visitor-toggle__track" aria-hidden="true">
              <span className="visitor-toggle__knob" />
            </span>
          </label>
        </div>
      )}

      <div className="visitor-wizard__actions">
        {/* "Keep booking", not "Back": on the one irreversible screen in the module the safe exit
            has to say what it does to the booking. */}
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t(isBulk ? "react_visitors_keep_bookings" : "react_visitors_keep_booking")}
        </button>

        <button
          type="button"
          className="visitor-btn visitor-btn--secondary visitor-btn--danger"
          onClick={onConfirm}
          // Nothing cancellable means nothing to do; the button would only produce an error.
          disabled={isSubmitting || cancellable.length === 0}
        >
          {t("react_visitors_cancel_confirm")}
        </button>
      </div>
    </div>
  );
};

export default VisitorCancelConfirm;
